/**
 * The grouping-chain editor for the rollup's configurable tab (PlanRollup-View-Shaping §9).
 *
 * An ordered breadcrumb of chips — outermost level first, because the order *is* the nesting. Levels are
 * removed with the chip's ✕, reordered by dragging, and added from a menu built at runtime from the four
 * fixed sources plus one entry per active DimensionDefinition, so a new customer dimension becomes
 * groupable with no code change.
 *
 * The chain is a display concern only: grouping re-partitions and never changes the total (§14), so
 * nothing here can make the figures disagree with the page's KPI tiles. That is the filter's problem,
 * and the filter is a later phase.
 */

import { useMemo, useState } from 'react';
import { Box, Button, Chip, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useDimensionDefinitions } from '@/hooks/finance/useDimensionDefinition';

/** Mirrors RollupGroupBy.MaxLevels — each level multiplies row cardinality. */
export const MAX_GROUP_LEVELS = 4;

const DIMENSION_PREFIX = 'DIMENSION:';

interface Props {
  /** The applied chain as server tokens, outermost first. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Reset target — the tab's preset, so "back to how it was" is one click. */
  defaultValue: string[];
}

export function GroupByChips({ value, onChange, defaultValue }: Props) {
  const { t } = useTranslation();
  const { data: dimensions } = useDimensionDefinitions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const fixedLabels: Record<string, string> = useMemo(
    () => ({
      ORG_NODE: t('Finance.PlanRollup.GroupByOrgNode', { defaultValue: 'Org node' }),
      ACCOUNT_GROUP: t('Finance.PlanRollup.GroupByAccountGroup', { defaultValue: 'Account group' }),
      ACCOUNT_TYPE: t('Finance.PlanRollup.GroupByAccountType', { defaultValue: 'Account type' }),
      ACCOUNT: t('Finance.PlanRollup.GroupByAccount', { defaultValue: 'Account' }),
    }),
    [t],
  );

  const activeDimensions = useMemo(
    () => (dimensions ?? []).filter((d) => d.isActive),
    [dimensions],
  );

  const labelFor = (token: string): string => {
    if (token.startsWith(DIMENSION_PREFIX)) {
      const id = token.slice(DIMENSION_PREFIX.length);
      // A dimension that has since been deactivated still has to render as something: its token beats
      // a blank chip, which would read as a level with no name rather than one that no longer exists.
      return activeDimensions.find((d) => d.id === id)?.name ?? token;
    }
    return fixedLabels[token] ?? token;
  };

  const available = useMemo(() => {
    const options = [
      ...Object.keys(fixedLabels).map((token) => ({ token, label: fixedLabels[token] })),
      ...activeDimensions.map((d) => ({ token: `${DIMENSION_PREFIX}${d.id}`, label: d.name })),
    ];
    // A level cannot repeat, so what is already in the chain is not offered again.
    return options.filter((o) => !value.includes(o.token));
  }, [fixedLabels, activeDimensions, value]);

  const atCap = value.length >= MAX_GROUP_LEVELS;
  const isDefault = value.length === defaultValue.length && value.every((v, i) => v === defaultValue[i]);

  const removeAt = (index: number) => onChange(value.filter((_, i) => i !== index));

  const moveTo = (from: number, to: number) => {
    if (from === to) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Finance.PlanRollup.GroupByLabel', { defaultValue: 'Group by' })}
      </Typography>

      {value.map((token, index) => (
        <Box
          key={token}
          draggable
          onDragStart={() => setDragIndex(index)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => {
            if (dragIndex !== null) moveTo(dragIndex, index);
            setDragIndex(null);
          }}
          onDragEnd={() => setDragIndex(null)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'grab' }}
        >
          {index > 0 && (
            <Typography component="span" variant="body2" color="text.disabled">
              ›
            </Typography>
          )}
          <Chip
            size="small"
            label={labelFor(token)}
            onDelete={() => removeAt(index)}
            sx={{ opacity: dragIndex === index ? 0.5 : 1 }}
          />
        </Box>
      ))}

      {value.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('Finance.PlanRollup.GroupByFlat', { defaultValue: 'No grouping — one row per account' })}
        </Typography>
      )}

      <Tooltip
        title={
          atCap
            ? t('Finance.PlanRollup.GroupByAtCap', {
                defaultValue: 'A grouping can have at most {{max}} levels.',
                max: MAX_GROUP_LEVELS,
              })
            : ''
        }
      >
        {/* A disabled button swallows pointer events, so the tooltip needs a wrapper to hang on. */}
        <span>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={atCap || available.length === 0}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ textTransform: 'none' }}
          >
            {t('Finance.PlanRollup.GroupByAddLevel', { defaultValue: 'Add level' })}
          </Button>
        </span>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {available.map((option) => (
          <MenuItem
            key={option.token}
            onClick={() => {
              onChange([...value, option.token]);
              setAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {!isDefault && (
        <Button size="small" onClick={() => onChange([...defaultValue])} sx={{ textTransform: 'none' }}>
          {t('Finance.PlanRollup.GroupByReset', { defaultValue: 'Reset' })}
        </Button>
      )}
    </Box>
  );
}

export default GroupByChips;
